import React from "react";
//#region types
import { AgentCardRow, AgentReply, AgentSentCase, SupportCaseContent } from "models/agent/agentContract";
import { StyleEnums } from "models/enums";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { KeyValue } from "models/utilTypes/keyValue";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { AgentPending } from "utils/helpers/agentConversationStore";
import { AgentMode } from "./agentFlow";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ActionLinkButton, DetailsTableViewSmall, Icon, StaticInfoMessage } from "components/common";
import AgentActions from "./agentActions";
import AgentAnswerCard from "./agentAnswerCard";
import AgentClarification from "./agentClarification";
import AgentPanel from "./agentPanel";
import AgentQuestionCard from "./agentQuestionCard";
import AgentWorkingLine from "./agentWorkingLine";
import { caseDetailRows } from "./supportCaseContent";
import useCurrentAgentConversation from "./useCurrentAgentConversation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

interface AgentQuestionViewProps {
  mode: Exclude<AgentMode, "ask">;
  question: string;
  choice?: string;
  /** She was shown an answer: the case goes because she asked, not because Wint could not answer. */
  afterAnswer: boolean;
  files: UploadedFile[];
  pending: AgentPending | null;
  reply?: AgentReply;
  review: SupportCaseContent;
  sendFailed: boolean;
  sentCase: AgentSentCase | null;
  onAnswer: (text: string, fromChoice: boolean) => void;
  onSkip: (reason: "none" | "skipped") => void;
  onNewQuestion: () => void;
  onSendToSupport: () => void;
  onGoToCase: (caseId: number) => void;
}

const t = LocalizationHelperInstance.getFormattedMessage;

const AnsweredStep: React.FC<{ reply: AgentReply; onSendToSupport: () => void; onNewQuestion: () => void }> = (
  props
) => (
  <>
    <AgentPanel label={t(TranslationKeys.SupportIssue.AgentFoundInWint)}>
      <AgentAnswerCard reply={props.reply} />
    </AgentPanel>
    <StaticInfoMessage displayType={StyleEnums.DisplayType.Info} fullWidth>
      {t(TranslationKeys.SupportIssue.AgentNotSentInfo)}
    </StaticInfoMessage>
    <AgentActions
      secondary={{
        labelKey: TranslationKeys.SupportIssue.AgentSendToSupport,
        onClick: props.onSendToSupport,
        testId: "agent-send-anyway",
      }}
      primary={{
        labelKey: TranslationKeys.SupportIssue.AgentNewQuestion,
        onClick: props.onNewQuestion,
        testId: "agent-new-question",
      }}
    />
  </>
);

const SentAlong: React.FC<{ rows: AgentCardRow[]; step: "review" | "sent"; lead?: string }> = ({
  rows,
  step,
  lead,
}) => {
  const [open, setOpen] = React.useState(true);
  const { conversation } = useCurrentAgentConversation();
  const toggle = () => {
    conversation.track("details.toggled", { open: !open, step });
    setOpen(!open);
  };

  return (
    <Grid container direction="column" className={cssExports.agentSentAlong}>
      {lead ? <p className={cssExports.agentCantAnswer}>{lead}</p> : null}
      <ActionLinkButton
        onClick={toggle}
        endIcon={
          <Icon icon={open ? IconDefinitionConstants.WChevronSmallUp : IconDefinitionConstants.WChevronSmallDown} />
        }
        testId="agent-sent-along"
      >
        {t(TranslationKeys.SupportIssue.AgentSentAlong)}
      </ActionLinkButton>
      {open ? (
        <DetailsTableViewSmall
          detailsList={rows.map((row) => new KeyValue<string | undefined, string>(row.label, row.value))}
          hideButton
        />
      ) : null}
    </Grid>
  );
};

const AgentQuestionView: React.FC<AgentQuestionViewProps> = (props) => {
  const { mode, pending, reply, sentCase, files } = props;
  const sending = pending?.kind === "send";

  const step = (() => {
    switch (mode) {
      case "working":
        return pending ? <AgentWorkingLine pending={pending} /> : null;
      case "answered":
        return reply ? (
          <AnsweredStep reply={reply} onSendToSupport={props.onSendToSupport} onNewQuestion={props.onNewQuestion} />
        ) : null;
      case "clarify":
        return reply?.kind === "answer" || reply?.kind === "question" ? (
          <AgentClarification text={reply.text} onAnswer={props.onAnswer} onSkip={props.onSkip} />
        ) : null;
      case "review":
        return (
          <>
            <SentAlong
              step="review"
              rows={caseDetailRows(
                props.review,
                files.map((file) => file.fileName),
                t
              )}
              lead={t(
                props.afterAnswer
                  ? TranslationKeys.SupportIssue.AgentSendLine
                  : TranslationKeys.SupportIssue.AgentCantAnswer
              )}
            />
            <StaticInfoMessage
              displayType={props.sendFailed ? StyleEnums.DisplayType.Danger : StyleEnums.DisplayType.Info}
              fullWidth
            >
              {t(
                props.sendFailed
                  ? TranslationKeys.SupportIssue.AgentCaseNotSent
                  : TranslationKeys.SupportIssue.AgentNotSentYetInfo
              )}
            </StaticInfoMessage>
            <AgentActions
              secondary={{
                labelKey: TranslationKeys.SupportIssue.AgentNewQuestion,
                onClick: props.onNewQuestion,
                testId: "agent-review-new-question",
                disabled: sending,
              }}
              primary={{
                labelKey: TranslationKeys.SupportIssue.AgentSendToSupport,
                onClick: props.onSendToSupport,
                testId: "agent-review-send",
                isSubmitting: sending,
              }}
            />
          </>
        );
      case "sent":
        return sentCase ? (
          <>
            <SentAlong step="sent" rows={sentCase.details} />
            <StaticInfoMessage displayType={StyleEnums.DisplayType.Success} fullWidth>
              <span className={cssExports.agentNoticeBody}>
                {t(TranslationKeys.SupportIssue.AgentSentNotice)}
                <ActionLinkButton
                  onClick={() => props.onGoToCase(sentCase.caseId)}
                  endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
                  testId="agent-open-case"
                >
                  {t(TranslationKeys.SupportIssue.AgentGoToCase)}
                </ActionLinkButton>
              </span>
            </StaticInfoMessage>
            <AgentActions
              primary={{
                labelKey: TranslationKeys.SupportIssue.AgentNewQuestion,
                onClick: props.onNewQuestion,
                testId: "agent-new-question",
              }}
            />
          </>
        ) : null;
    }
  })();

  return (
    <Grid container direction="column" className={cssExports.agentQuestionView}>
      <AgentQuestionCard text={props.question} files={files} choice={props.choice} />
      {step}
    </Grid>
  );
};

export default AgentQuestionView;
